import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CoreUiActions from './core-ui.actions';
import { CoreUiEntity } from './core-ui.models';
import { Customer, CUSTOMER_TYPE, Colour, KEYS, UserProfile, BrandDefaults, OpenOrder } from '@pos-app/data';

export const COREUI_FEATURE_KEY = 'coreUi';

export interface State extends EntityState<CoreUiEntity> {
  selectedId?: string | number; // which CoreUi record has been selected
  hasUnsavedChanges?: boolean; // which CoreUi record has been selected
  loaded: boolean; // has the CoreUi list been loaded
  error?: string | null; // last none error (if any)
  loginDetails?: UserProfile;
  searchingCustomers: boolean;
  searchingExternalCustomers: boolean;
  searchingBillToCustomers: boolean;
  customers: Customer[];
  externalCustomers: Customer[];
  billToCustomers: Customer[];
  hasMoreRecords: boolean;
  hasMoreBillToRecords: boolean;
  hasActiveOrder: boolean;
  activeOrder: { orderNumber: string; quickSaveAllowed: boolean };
  openOrder: OpenOrder;
  colourList: Colour[];
  selectedVehicle: any;
  cashDrawer: string;
  customerSearchValue: string;
  hasPendingPayments: boolean;
  hasPendingLineChangeTF: boolean;
  brandDefaults: BrandDefaults;
  isIncompleteOrder: boolean;
  hasLoadedGiftCards: boolean;
  alertCount: number;
  isShowRoomView: boolean;
  isXRefPartsView: boolean;
  isOrderChanged: boolean;
  parkedOrderListLength: number;
  cachedCommunicationGroupList: any;
  arbCentralNavSecurity: any;
  cachedSiteList: any;
  homeSite: any;
  customerOrderDetailsForTask: any;
  cachedSalesPeopleList: any;
  shipToCustomers: Customer[];
  hasMoreRecordsShipTo: boolean;
  searchingShipToCustomers: boolean;
  jobOrders?: any[];
  hasMoreJobOrderRecords?: boolean;
  searchingJobOrder?: boolean;
}

export interface CoreUiPartialState {
  readonly [COREUI_FEATURE_KEY]: State;
}

export const coreUiAdapter: EntityAdapter<CoreUiEntity> = createEntityAdapter<CoreUiEntity>();

export const initialState: State = coreUiAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  searchingCustomers: false,
  searchingExternalCustomers: false,
  searchingBillToCustomers: false,
  customers: [],
  externalCustomers: [],
  billToCustomers: [],
  hasMoreRecords: false,
  hasMoreBillToRecords: false,
  hasActiveOrder: false,
  activeOrder: null,
  openOrder: null,
  colourList: [],
  selectedVehicle: null,
  cashDrawer: localStorage.getItem('cashDrawer'),
  customerSearchValue: '',
  hasPendingPayments: false,
  hasPendingLineChangeTF: false,
  brandDefaults: null,
  isIncompleteOrder: false,
  hasLoadedGiftCards: false,
  alertCount: 0,
  isShowRoomView: false,
  isXRefPartsView: false,
  isOrderChanged: false,
  parkedOrderListLength: 0,
  cachedCommunicationGroupList: null,
  arbCentralNavSecurity: null,
  cachedSiteList: [],
  homeSite: null,
  cachedSalesPeopleList: [],
  customerOrderDetailsForTask: null,
  shipToCustomers: [],
  hasMoreRecordsShipTo: false,
  searchingShipToCustomers: false,
});

const coreUiReducer = createReducer(
  initialState,
  on(CoreUiActions.loadCoreUi, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(CoreUiActions.loadCoreUiSuccess, (state, { coreUi }) => coreUiAdapter.setAll(coreUi, { ...state, loaded: true })),
  on(CoreUiActions.loadCoreUiFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(CoreUiActions.hasUnsavedChanges, (state, { unsavedChanges }) => ({
    ...state,
    hasUnsavedChanges: unsavedChanges,
  })),

  on(CoreUiActions.clearUnsavedChanges, (state, { unsavedChanges }) => ({
    ...state,
    hasUnsavedChanges: unsavedChanges,
  })),

  on(CoreUiActions.loginSuccess, (state, { payload }) => ({
    ...state,
    loginDetails: payload,
  })),

  on(CoreUiActions.searchCustomers, (state, action) => {
    const searchingKey = action.customerType === CUSTOMER_TYPE.billTo.toLowerCase() ? 'searchingBillToCustomers' : 'searchingCustomers';
    const hasMoreRecordsKey = action.customerType === CUSTOMER_TYPE.billTo.toLowerCase() ? 'hasMoreBillToRecords' : 'hasMoreRecords';

    return {
      ...state,
      [searchingKey]: true,
      [hasMoreRecordsKey]: false,
    };
  }),
  on(CoreUiActions.searchCustomersFailed, (state) => {
    const searchingKey = state.searchingBillToCustomers ? 'searchingBillToCustomers' : 'searchingCustomers';
    return {
      ...state,
      [searchingKey]: false,
    };
  }),
  on(CoreUiActions.searchCustomersSuccess, (state, { result }) => {
    const searchingKey = state.searchingBillToCustomers ? 'searchingBillToCustomers' : 'searchingCustomers';
    const listKey = state.searchingBillToCustomers ? 'billToCustomers' : 'customers';
    const hasMoreRecordsKey = state.searchingBillToCustomers ? 'hasMoreBillToRecords' : 'hasMoreRecords';
    return {
      ...state,
      [listKey]: result.SearchResults,
      [searchingKey]: false,
      [hasMoreRecordsKey]: result.moreRecordsYN === 'Y',
    };
  }),
  on(CoreUiActions.clearSearchData, (state) => ({
    ...state,
    customers: [],
    billToCustomers: [],
    searchingCustomers: false,
    searchingBillToCustomers: false,
    hasMoreRecords: false,
    hasMoreBillToRecords: false,
  })),

  on(CoreUiActions.clearSearchJobOrderData, (state) => ({
    ...state,
    jobOrders: [],
    searchingJobOrder: false,
    hasMoreJobOrderRecords: false,
  })),

  on(CoreUiActions.searchExternalCustomers, (state, action) => {
    const searchingKey = 'searchingExternalCustomers';
    const hasMoreRecordsKey = 'hasMoreRecords';
    return {
      ...state,
      [searchingKey]: true,
      [hasMoreRecordsKey]: false,
    };
  }),
  on(CoreUiActions.searchExternalCustomersFailed, (state) => {
    const searchingKey = 'searchingExternalCustomers';
    return {
      ...state,
      [searchingKey]: false,
    };
  }),
  on(CoreUiActions.searchExternalCustomersSuccess, (state, { result }) => {
    const hasMoreRecordsKey = 'hasMoreRecords';
    const listKey = 'externalCustomers';
    const searchingKey = 'searchingExternalCustomers';

    return {
      ...state,
      [listKey]: result.SearchResults,
      [searchingKey]: false,
      [hasMoreRecordsKey]: result.moreRecordsYN === 'Y',
    };
  }),
  on(CoreUiActions.clearExternalSearchData, (state) => ({
    ...state,
    externalCustomers: [],
    billToCustomers: [],
    searchingCustomers: false,
    searchingBillToCustomers: false,
    hasMoreRecords: false,
    hasMoreBillToRecords: false,
  })),

  on(CoreUiActions.hasActiveOrder, (state, { orderNumber, quickSaveAllowed = false }) => ({
    ...state,
    hasActiveOrder: !!orderNumber,
    activeOrder: { orderNumber, quickSaveAllowed },
  })),

  on(CoreUiActions.setOpenOrder, (state, { openOrder, quickSaveAllowed }) => {
    return {
      ...state,
      openOrder: openOrder,
      hasActiveOrder: !!openOrder?.orderNumber,
      activeOrder: { orderNumber: openOrder?.orderNumber, quickSaveAllowed: quickSaveAllowed },
    };
  }),

  on(CoreUiActions.setSelectedVehicle, (state, { customerVehicleID, partsVehicleID, vehicleDescription, jdeVehicleID }) => ({
    ...state,
    selectedVehicle: {
      partsVehicleID,
      vehicleDescription,
      customerVehicleID,
      jdeVehicleID,
    },
  })),

  on(CoreUiActions.loadColourList, (state, { colourList }) => ({
    ...state,
    colourList,
  })),

  on(CoreUiActions.setCashDrawer, (state, { cashDrawer }) => {
    localStorage.setItem(KEYS.cashDrawer, cashDrawer);
    return {
      ...state,
      cashDrawer,
    };
  }),
  on(CoreUiActions.hasPendingPayments, (state, { hasPendingPayments }) => {
    return {
      ...state,
      hasPendingPayments,
    };
  }),

  on(CoreUiActions.hasPendingLineChangeTF, (state, { hasPendingLineChangeTF }) => {
    return {
      ...state,
      hasPendingLineChangeTF,
    };
  }),
  on(CoreUiActions.loadBrandDefaultsSuccess, (state, { result }) => ({
    ...state,
    brandDefaults: result,
  })),
  on(CoreUiActions.hasIncompleteOrder, (state, { isIncompleteOrder }) => ({
    ...state,
    isIncompleteOrder,
  })),
  on(CoreUiActions.hasLoadedGiftCard, (state, { hasLoadedGiftCards }) => ({
    ...state,
    hasLoadedGiftCards,
  })),
  on(CoreUiActions.resetAllFlags, (state) => ({
    ...state,
    hasActiveOrder: false,
    activeOrder: null,
    hasLoadedGiftCards: false,
    hasPendingPayments: false,
    isIncompleteOrder: false,
  })),
  on(CoreUiActions.checkIsShowroomView, (state, { isShowRoomView }) => ({
    ...state,
    isShowRoomView,
  })),

  on(CoreUiActions.checkIsXRefPartsView, (state, { isXRefPartsView }) => ({
    ...state,
    isXRefPartsView,
  })),
  on(CoreUiActions.hasOrderChanged, (state, { isOrderChanged }) => ({
    ...state,
    isOrderChanged,
  })),
  on(CoreUiActions.parkedOrderListLength, (state, { parkedOrderListLength }) => ({
    ...state,
    parkedOrderListLength,
  })),

  on(CoreUiActions.loadCommunicationGroupListSuccess, (state, { communicationGroupList }) => ({
    ...state,
    cachedCommunicationGroupList: communicationGroupList,
  })),

  on(CoreUiActions.loadArbCentralNavSecuritySuccess, (state, { arbCentralNavSecurity }) => ({
    ...state,
    arbCentralNavSecurity: arbCentralNavSecurity,
  })),
  on(CoreUiActions.fetchSiteListSuccess, (state, { cachedSiteList }) => ({
    ...state,
    cachedSiteList: cachedSiteList,
  })),
  on(CoreUiActions.fetchSalesPeopleListSuccess, (state, { cachedSalesPeopleList }) => ({
    ...state,
    cachedSalesPeopleList: cachedSalesPeopleList,
  })),
  on(CoreUiActions.saveCustomerOrderDetailsForTask, (state, data) => ({
    ...state,
    customerOrderDetailsForTask: data,
  })),

  // Ship To Customers
  on(CoreUiActions.searchshipToCustomers, (state, action) => {
    const searchingKey = 'searchingShipToCustomers';
    const hasMoreRecordsKey = 'hasMoreRecordsShipTo';
    return {
      ...state,
      [searchingKey]: true,
      [hasMoreRecordsKey]: false,
    };
  }),
  on(CoreUiActions.searchshipToCustomersSuccess, (state, { result }) => {
    const hasMoreRecordsKey = 'hasMoreRecordsShipTo';
    const listKey = 'shipToCustomers';
    const searchingKey = 'searchingShipToCustomers';
    return {
      ...state,
      [listKey]: result.SearchResults,
      [searchingKey]: false,
      [hasMoreRecordsKey]: result.moreRecordsYN === 'Y',
    };
  }),
  on(CoreUiActions.searchshipToCustomersFailed, (state) => {
    const searchingKey = 'searchingShipToCustomers';
    return {
      ...state,
      [searchingKey]: false,
    };
  }),
  on(CoreUiActions.clearshipToSearchData, (state) => ({
    ...state,
    shipToCustomers: [],
    hasMoreRecordsShipTo: false,
  })),

  on(CoreUiActions.searchJobOrder, (state, action) => {
    return {
      ...state,
      searchingJobOrder: true,
      hasMoreJobOrderRecords: false,
    };
  }),

  on(CoreUiActions.searchJobOrderSuccess, (state, { result }) => {
    const searchingKey = 'searchingJobOrder';
    const listKey = 'jobOrders';
    const hasMoreRecordsKey = 'hasMoreJobOrderRecords';
    return {
      ...state,
      [listKey]: result?.jobList,
      [searchingKey]: false,
      [hasMoreRecordsKey]: result?.moreRecordsYN === 'Y',
    };
  })
);

export function reducer(state: State | undefined, action: Action) {
  return coreUiReducer(state, action);
}

